import { TaxonomicStorage } from './storage.js';

/**
 * Monthly calendar view for browsing puzzles.
 */
export class PuzzleCalendar {
    constructor(onDateSelect) {
        this.onDateSelect = onDateSelect; // callback(dateStr, isCompleted)
        this.currentMonth = new Date();
        this.completionData = {};
        this.availablePuzzles = new Set();
        this.container = null;
    }

    async init() {
        this.loadCompletionData();
        await this.loadAvailablePuzzles();
    }

    loadCompletionData() {
        this.completionData = TaxonomicStorage.getAllCompletionData();
    }

    async loadAvailablePuzzles() {
        try {
            const response = await fetch('puzzles/manifest.json');
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const manifest = await response.json();
            if (!manifest || !Array.isArray(manifest.puzzles)) {
                throw new Error('Invalid manifest format');
            }
            // Filter to only valid date strings
            const validDates = manifest.puzzles.filter(d => /^\d{4}-\d{2}-\d{2}$/.test(d));
            this.availablePuzzles = new Set(validDates);
        } catch (e) {
            console.error('Failed to load puzzle manifest:', e);
            this.availablePuzzles = new Set();
        }
    }

    render(container) {
        this.container = container;
        container.innerHTML = '';
        const header = this.renderHeader();
        const grid = this.renderGrid();
        container.appendChild(header);
        container.appendChild(grid);
    }

    renderHeader() {
        const header = document.createElement('div');
        header.className = 'calendar-header';

        const today = new Date();
        const viewingMonth = this.currentMonth.getMonth();
        const viewingYear = this.currentMonth.getFullYear();

        const canGoForward = viewingYear < today.getFullYear() ||
            (viewingYear === today.getFullYear() && viewingMonth < today.getMonth());
        const canGoBack = this.hasEarlierPuzzles();

        const prevBtn = document.createElement('button');
        prevBtn.className = 'calendar-nav-btn';
        prevBtn.textContent = '\u2190';
        prevBtn.disabled = !canGoBack;
        prevBtn.setAttribute('aria-label', 'Previous month');
        prevBtn.addEventListener('click', () => this.navigateMonth(-1));

        const monthLabel = document.createElement('span');
        monthLabel.className = 'calendar-month-label';
        monthLabel.textContent = this.currentMonth.toLocaleDateString('en-US', {
            month: 'long', year: 'numeric'
        });

        const nextBtn = document.createElement('button');
        nextBtn.className = 'calendar-nav-btn';
        nextBtn.textContent = '\u2192';
        nextBtn.disabled = !canGoForward;
        nextBtn.setAttribute('aria-label', 'Next month');
        nextBtn.addEventListener('click', () => this.navigateMonth(1));

        header.appendChild(prevBtn);
        header.appendChild(monthLabel);
        header.appendChild(nextBtn);
        return header;
    }

    hasEarlierPuzzles() {
        const viewStart = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 1);
        for (const dateStr of this.availablePuzzles) {
            // Validate date string format
            const parts = dateStr.split('-');
            if (parts.length !== 3) continue;
            const [year, month] = parts.map(Number);
            if (isNaN(year) || isNaN(month) || month < 1 || month > 12) continue;
            const puzzleDate = new Date(year, month - 1, 1);
            if (puzzleDate < viewStart) return true;
        }
        return false;
    }

    navigateMonth(delta) {
        const newMonth = new Date(this.currentMonth);
        newMonth.setMonth(newMonth.getMonth() + delta);
        const today = new Date();
        if (newMonth > today) {
            newMonth.setMonth(today.getMonth());
            newMonth.setFullYear(today.getFullYear());
        }
        this.currentMonth = newMonth;
        this.render(this.container);
    }

    renderGrid() {
        const grid = document.createElement('div');
        grid.className = 'calendar-grid';

        const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        dayLabels.forEach(day => {
            const label = document.createElement('div');
            label.className = 'calendar-day-label';
            label.textContent = day;
            grid.appendChild(label);
        });

        const year = this.currentMonth.getFullYear();
        const month = this.currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        for (let i = 0; i < firstDay.getDay(); i++) {
            const empty = document.createElement('div');
            empty.className = 'calendar-day empty';
            grid.appendChild(empty);
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (let day = 1; day <= lastDay.getDate(); day++) {
            const date = new Date(year, month, day);
            const dateStr = this.formatDate(date);

            const cell = document.createElement('button');
            cell.className = 'calendar-day';

            const isFuture = date > today;
            const hasPuzzle = this.availablePuzzles.has(dateStr);
            const completion = this.completionData[dateStr];

            if (isFuture) {
                cell.classList.add('future');
                cell.disabled = true;
                cell.textContent = day;
            } else if (!hasPuzzle) {
                cell.classList.add('no-puzzle');
                cell.disabled = true;
                cell.textContent = day;
            } else {
                cell.classList.add('available');
                if (completion?.completed) {
                    cell.classList.add('completed');
                    cell.innerHTML = `${day}<span class="calendar-check">\u2713</span>`;
                } else if (completion?.inProgress) {
                    cell.classList.add('in-progress');
                    cell.textContent = day;
                } else {
                    cell.textContent = day;
                }
                cell.addEventListener('click', () => {
                    this.onDateSelect(dateStr, completion?.completed || false);
                });
            }

            if (date.getTime() === today.getTime()) {
                cell.classList.add('today');
            }

            grid.appendChild(cell);
        }

        return grid;
    }

    formatDate(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    refresh() {
        this.loadCompletionData();
        if (this.container) {
            this.render(this.container);
        }
    }
}
